import { useEffect, useState } from "react";
import debounce from "lodash/debounce";

import {
  type TailwindBreakpoints,
  useDeviceWidth,
} from "@vega-core/hooks/useDeviceWidth";

/**
 * Returns true when the viewport is below a given breakpoint (mobile) and
 * currently in landscape orientation.
 * - Reuses `useDeviceWidth` to gate by a breakpoint (default: "md")
 * - Listens to orientation via `matchMedia('(orientation: landscape)')` with
 *   a resize fallback, debounced for performance
 * - Handles SSR by guarding against `window` access
 */
export function useMobileLandscape(
  mobileMaxBreakpoint: TailwindBreakpoints = "md",
): boolean {
  // mobile is when viewport width is below the provided breakpoint
  const isAtLeastBreakpoint = useDeviceWidth(mobileMaxBreakpoint);
  const isMobile = !isAtLeastBreakpoint;

  const [isLandscape, setIsLandscape] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const mql =
      typeof window.matchMedia === "function"
        ? window.matchMedia("(orientation: landscape)")
        : undefined;

    const compute = () => {
      const bySize = window.innerWidth > window.innerHeight;
      const byMedia = mql ? mql.matches : false;
      setIsLandscape(byMedia || bySize);
    };

    const debouncedCompute = debounce(compute, 150);

    // Initial evaluation
    debouncedCompute();

    // Prefer orientation media query changes when available
    const mqlListener = () => debouncedCompute();

    if (mql) {
      if (typeof mql.addEventListener === "function") {
        mql.addEventListener("change", mqlListener);
      } else if (typeof mql.addListener === "function") {
        mql.addListener(mqlListener);
      }
    }

    // Fallback to resize/orientationchange
    window.addEventListener("orientationchange", debouncedCompute);
    window.addEventListener("resize", debouncedCompute);

    return () => {
      if (mql) {
        if (typeof mql.removeEventListener === "function") {
          mql.removeEventListener("change", mqlListener);
        } else if (typeof mql.removeListener === "function") {
          mql.removeListener(mqlListener);
        }
      }

      window.removeEventListener("orientationchange", debouncedCompute);
      window.removeEventListener("resize", debouncedCompute);
      debouncedCompute.cancel();
    };
  }, []);

  return isMobile && isLandscape;
}
